import { useState } from 'react';
import { Link } from 'react-router-dom';
import { allComics, categories, categorySlugs } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import { siteInfo } from '@/mocks/site';

export function HeroSection() {
  return (
    <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20background%20with%20soft%20gradient%20from%20warm%20cream%20to%20dusty%20rose%20to%20muted%20mauve%2C%20smooth%20flowing%20shapes%2C%20elegant%20minimalist%20composition%2C%20subtle%20geometric%20patterns%2C%20dreamy%20atmospheric%20lighting%2C%20no%20text%2C%20pure%20abstract%20art%2C%20high%20resolution%2C%20editorial%20quality%2C%20warm%20and%20inviting%20tone&width=1920&height=1080&seq=hero-bg-main-01&orientation=landscape"
          alt="虚空韩漫网"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/40"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-16 max-w-7xl mx-auto py-16 md:py-20">
          <div className="flex-1 w-full text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-medium mb-6">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sparkling-2-line text-white"></i>
              </span>
              每日更新 · 免费阅读
            </div>
            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
              虚空韩漫网
            </h1>
            <p className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white/90 mt-3 leading-snug">
              韩漫在线阅读
            </p>
            <p className="text-white/70 text-base md:text-lg mt-5 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              免费的韩国漫画大全 · 无删减下拉式韩漫 · 强大的搜索引擎助您快速定位心仪作品
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 justify-center lg:justify-start">
              <Link to="/category/all" className="px-8 py-3 rounded-full bg-white text-foreground-950 font-semibold text-sm hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer">
                开始阅读
              </Link>
              <Link to="/about" className="px-8 py-3 rounded-full border border-white/40 text-white font-medium text-sm hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                了解更多
              </Link>
            </div>
          </div>

          <div className="flex-1 w-full max-w-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20abstract%20illustration%20of%20floating%20comic%20book%20pages%20with%20soft%20gradient%20colors%2C%20dreamy%20pastel%20tones%2C%20books%20and%20pages%20floating%20in%20air%2C%20whimsical%20storytelling%20vibe%2C%20artistic%20composition%2C%20warm%20cream%20and%20rose%20color%20palette%2C%20soft%20shadows%2C%20editorial%20quality%2C%20no%20text%2C%20clean%20aesthetic&width=800&height=600&seq=hero-illustration-01&orientation=landscape"
              alt="韩漫在线阅读"
              className="w-full h-auto rounded-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export function StatsBanner() {
  const stats = [
    { value: siteInfo.stats.totalComics, label: '漫画作品', icon: 'ri-book-2-line' },
    { value: siteInfo.stats.dailyUsers, label: '日活用户', icon: 'ri-user-heart-line' },
    { value: siteInfo.stats.monthlyViews, label: '月阅读量', icon: 'ri-eye-line' },
    { value: siteInfo.stats.totalChapters, label: '章节总数', icon: 'ri-file-list-3-line' },
  ];

  return (
    <section className="bg-background-50 py-10 md:py-14">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center py-6 px-4 rounded-xl bg-background-100">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${stat.icon} text-accent-600 text-lg`}></i>
              </div>
              <div className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">{stat.value}</div>
              <div className="text-foreground-500 text-xs mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CategoryNav() {
  return (
    <section className="bg-background-100 py-10 md:py-14">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-foreground-500 text-xs font-medium tracking-wider uppercase mb-2">探索分类</p>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">漫画分类导航</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {categorySlugs.map((slug) => {
            const cat = categories[slug];
            return (
              <Link
                key={slug}
                to={`/category/${slug}`}
                className="group flex flex-col items-center gap-3 p-6 rounded-xl bg-background-50 hover:bg-accent-50 transition-all cursor-pointer border border-transparent hover:border-accent-200/60"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-accent-600 text-xl`}></i>
                </div>
                <span className="font-medium text-sm text-foreground-800 group-hover:text-accent-700 whitespace-nowrap">{cat.name}</span>
                <span className="text-foreground-400 text-xs text-center">{cat.description}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FeaturedComics({ title, subtitle, comics, showViewAll = false, viewAllLink }: { title: string; subtitle?: string; comics: Comic[]; showViewAll?: boolean; viewAllLink?: string }) {
  return (
    <section className="bg-background-50 py-10 md:py-14">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">{title}</h2>
            {subtitle && <p className="text-foreground-500 text-sm mt-2">{subtitle}</p>}
          </div>
          {showViewAll && viewAllLink && (
            <Link to={viewAllLink} className="text-primary-500 hover:text-primary-600 text-sm font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
          {comics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function ComicCard({ comic }: { comic: Comic }) {
  const [imgError, setImgError] = useState(false);

  return (
    <Link
      to={`/comic/${comic.slug}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
        {!imgError ? (
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`虚空韩漫网 - ${comic.title}`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-background-200 text-foreground-400">
            <div className="w-8 h-8 flex items-center justify-center mb-2">
              <i className="ri-image-line text-2xl"></i>
            </div>
            <span className="text-xs">{comic.title}</span>
          </div>
        )}
        <div className="absolute top-2 left-2">
          {comic.status === '已完结' ? (
            <span className="px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-medium">已完结</span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-primary-500 text-white text-[10px] font-medium">连载中</span>
          )}
        </div>
        <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium">
          {comic.rating}
        </div>
      </div>
      <h3 className="font-heading font-semibold text-sm text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">{comic.title}</h3>
      <p className="text-foreground-400 text-xs mt-1">{comic.author}</p>
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        {comic.tags.slice(0, 2).map((tag) => (
          <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px]">{tag}</span>
        ))}
      </div>
    </Link>
  );
}

export function LatestUpdates() {
  const sorted = [...allComics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 6);

  return (
    <section className="bg-background-100 py-10 md:py-14">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">最新更新</h2>
          <p className="text-foreground-500 text-sm mt-2">每日更新热门韩漫，精彩不容错过</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.slug}`}
              className="flex gap-4 p-4 rounded-xl bg-background-50 hover:bg-accent-50 transition-all cursor-pointer border border-transparent hover:border-accent-200/60"
            >
              <div className="w-16 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-sm text-foreground-950 line-clamp-1">{comic.title}</h3>
                <p className="text-foreground-400 text-xs mt-1">作者：{comic.author}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-foreground-500 text-[10px] flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {(comic.views / 10000).toFixed(0)}万
                  </span>
                  <span className="text-accent-600 text-[10px] font-medium">更新至第{comic.chapters.length}话</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SearchSection() {
  const [query, setQuery] = useState('');

  return (
    <section className="relative bg-foreground-950 py-16 md:py-20 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20gradient%20background%20with%20subtle%20geometric%20patterns%2C%20deep%20charcoal%20tone%2C%20minimalist%20design%2C%20soft%20texture%2C%20elegant%20dark%20atmosphere%2C%20no%20text%2C%20clean%20abstract%20composition%2C%20high%20resolution&width=1920&height=600&seq=search-bg-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative z-10 text-center px-4 md:px-6">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">找不到想要的漫画？</h2>
        <p className="text-foreground-300 text-sm mb-8">强大的搜索引擎，帮您快速定位心仪作品</p>
        <div className="max-w-xl mx-auto flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="输入漫画名称、作者或关键词..."
            className="flex-1 px-5 py-3 rounded-full bg-foreground-800 border border-foreground-700 text-white text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400"
          />
          <button className="px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors whitespace-nowrap cursor-pointer">
            搜索
          </button>
        </div>
      </div>
    </section>
  );
}

export function HomeFAQ() {
  const faqs = [
    { q: '虚空韩漫网的漫画都是免费的吗？', a: '是的！虚空韩漫网上的所有韩国漫画都完全免费，无需付费即可畅享海量韩漫资源。' },
    { q: '什么是下拉式韩漫？如何阅读？', a: '下拉式韩漫是韩国漫画的特色阅读方式，您只需向下滑动页面即可连续阅读，无需翻页。' },
    { q: '网站上的漫画多久更新一次？', a: '虚空韩漫网每天都会更新大量最新漫画章节，热门连载作品通常每周更新1-2话。' },
  ];

  return (
    <section className="bg-background-50 py-10 md:py-14">
      <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">常见问题</h2>
          <p className="text-foreground-500 text-sm mt-2">关于虚空韩漫网的常见问题解答</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <details key={i} className="group bg-background-100 rounded-xl p-5 cursor-pointer">
              <summary className="font-heading font-semibold text-sm text-foreground-950 flex items-center justify-between list-none">
                {faq.q}
                <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                </span>
              </summary>
              <p className="text-foreground-600 text-sm mt-3 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}